let newClientOidSequence = -1;
let newOidSequence = 1;

const regex = /^(\d+).(\d+)$/;
const newOidRegex = /^([-]\d+)$/;

// Various arrangements of the class number, instance number, and random bytes.
// Note: This MUST match the C# code in Siza.StaffCare.Utils.OidObfuscator.
const schemes = [
	[12, 5, 6, 0, 2, 4, 8, 10, 7, 9, 1, 13, 11, 3],
	[9, 7, 5, 0, 8, 1, 6, 3, 4, 13, 11, 2, 12, 10],
	[12, 0, 1, 13, 11, 2, 3, 6, 8, 10, 5, 9, 7, 4],
	[5, 4, 0, 6, 8, 12, 7, 10, 11, 2, 13, 3, 1, 9],
	[9, 12, 13, 2, 3, 8, 0, 6, 5, 10, 1, 11, 4, 7],
	[5, 10, 4, 12, 2, 8, 1, 11, 13, 9, 7, 6, 3, 0],
	[4, 5, 3, 1, 13, 11, 8, 12, 6, 10, 0, 7, 2, 9],
	[10, 12, 8, 5, 3, 0, 2, 7, 13, 4, 11, 9, 6, 1],
	[6, 7, 1, 11, 5, 13, 4, 12, 2, 8, 9, 0, 3, 10],
	[12, 1, 2, 7, 0, 6, 9, 11, 3, 8, 13, 10, 4, 5],
	[7, 9, 1, 10, 5, 0, 3, 2, 6, 12, 13, 8, 11, 4],
	[1, 2, 9, 3, 13, 10, 5, 0, 7, 4, 12, 6, 8, 11],
	[7, 9, 4, 2, 10, 11, 6, 13, 1, 12, 5, 8, 0, 3],
	[12, 7, 2, 3, 11, 1, 8, 13, 10, 5, 0, 9, 6, 4],
];

const random = () => {
	return Math.floor(Math.random() * 256);
};

const memoizer = new Map<string, string>();

/**
 *  Takes a Jade OID and converts to a 16 digit hex string, containing the class number and instance number plus some
 *  random stuff and a LRC to detect corruption/hacking.
 */
export const obfuscate = (oid: string): string => {
	const cachedValue = memoizer.get(oid);
	if (cachedValue) {
		return cachedValue;
	}

	let classNum: number;
	let instanceNum: number;

	const newOidMatch = newOidRegex.exec(oid);
	if (newOidMatch) {
		classNum = 0;
		instanceNum = Number(newOidMatch[1]);
	} else {
		const match = regex.exec(oid);
		if (!match || match.length !== 3) {
			throw `Invalid OID input: ${oid}`;
		}

		classNum = Number(match[1]);
		instanceNum = Number(match[2]);
	}

	const classBytesRaw = intToByteArray(classNum);
	const classBytes = [classBytesRaw[0], classBytesRaw[1], classBytesRaw[2]];

	const instanceBytes = longToByteArray(instanceNum);

	const randomBytes = [random(), random(), random()];

	// Choose 1 of 14 schemes (14 different arrangements of 14 bytes)
	const scheme = randomBytes[0] % schemes.length;
	const schemeArrangement = schemes[scheme];

	// 14 bytes
	const combinedBytes = [...classBytes, ...instanceBytes, ...randomBytes];

	// Compute the redundancy check.
	const lrc = computeLrc(combinedBytes);

	const resultBuffer = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
	for (let i = 0; i < combinedBytes.length; i++) {
		resultBuffer[i + 1] = combinedBytes[schemeArrangement[i]];
	}

	resultBuffer[0] = scheme;
	resultBuffer[15] = lrc;

	const result = resultBuffer.reduce((str, value) => {
		str += value.toString(16).padStart(2, '0');
		return str;
	}, '');

	memoizer.set(oid, result);
	return result;
};

/**
 * Creates a fake OID with 0 as the class number, and obfuscates it.
 * Any OID with 0.nnnnn is considers a new OID, which the server will consider empty.
 * @returns
 */
export const obfuscateNew = (): string => {
	return obfuscate(`0.${newOidSequence++}`);
};

/**
 * Returns whether the obfuscated OID is a new OID.
 * @param obfuscatedOid
 * @returns
 */
export const isNewOid = (obfuscatedOid: string): boolean => {
	if (!obfuscatedOid) {
		return false;
	}

	const decoded = decode(obfuscatedOid);
	return isNewRealOid(decoded);
};

export const isNewRealOid = (oid: string): boolean => {
	const decodedAsNumber = Number(oid);
	const isCreate = decodedAsNumber < 1 && decodedAsNumber > 0;
	return isCreate;
};

/**
 * Constructs a new client-side OID, without a class number. Used only for multiple client-side objects, which are later
 * reconciled with the server. E.g. soft form fields.
 * @returns
 */
export const newClientOid = (): string => {
	return `${newClientOidSequence--}`;
};

/**
 * Determines whether the given string is a client-side OID.
 * @param oid
 * @returns
 */
export const isClientOid = (oid: string): boolean => {
	return Number(oid) < 0;
};

/**
 * Decodes an obfuscated OID into its real Jade OID.
 * @param obfuscated
 * @returns
 */
export const decode = (obfuscated: string): string => {
	const bytes = hexToBytes(obfuscated);
	if (bytes.length != 16) {
		throw new Error('Unexpected length ' + bytes.length + ' decoding "' + obfuscated + '"');
	}

	const scheme = bytes[0];
	const lrc = bytes[15];

	bytes[0] = 0;
	bytes[15] = 0;

	const computed = computeLrc(bytes);
	if (lrc != computed) {
		throw new Error('LRC failure');
	}

	const extractedBytes: number[] = [];
	const schemeArrangement = schemes[scheme];

	// 	// Reverse the arrangement back to the original.
	for (let i = 0; i < schemeArrangement.length; i++) {
		extractedBytes[schemeArrangement[i]] = bytes[i + 1];
	}

	const classNum = byteArrayToNumber(extractedBytes.slice(0, 3));
	let instanceNum = byteArrayToNumber(extractedBytes.slice(3, 10));

	// Handle -ve number (if top bit is set)
	if (instanceNum & 0x800000) {
		instanceNum = -(0xffffff - instanceNum);

		// Return just the instance number, not class.
		return String(instanceNum);
	}

	return `${classNum}.${instanceNum}`;
};

/**
 * Attempts to decode an obfuscated OID, returning null if it fails.
 */
export const tryDecode = (obfuscated: string): string | null => {
	try {
		return decode(obfuscated);
	} catch (e) {
		return null;
	}
};

const hexToBytes = (hex: string) => {
	const bytes: number[] = [];
	for (let c = 0; c < hex.length; c += 2) {
		const sub = hex.substring(c, c + 2);
		const num = parseInt(sub, 16);
		bytes.push(num);
	}

	return bytes;
};

const byteArrayToNumber = (bytes: number[]) => {
	let num = 0;
	for (let i = 0; i < bytes.length; i++) {
		num = num + (bytes[i] << (8 * i));
	}

	return num;
};

const longToByteArray = (long: number) => {
	// we want to represent the input as a 8-bytes array
	const byteArray = [0, 0, 0, 0, 0, 0, 0, 0];

	for (let index = 0; index < byteArray.length; index++) {
		const byte = long & 0xff;
		byteArray[index] = byte;
		long = (long - byte) / 256;
	}

	return byteArray;
};

const intToByteArray = (int: number) => {
	// we want to represent the input as a 8-bytes array
	const byteArray = [0, 0, 0, 0];

	for (let index = 0; index < byteArray.length; index++) {
		const byte = int & 0xff;
		byteArray[index] = byte;
		int = (int - byte) / 0x100;
	}

	return byteArray;
};

/**
 * Computes a longitudinal redundancy check.
 * @param combinedBytes
 * @returns
 */
const computeLrc = (combinedBytes: number[]) => {
	let lrc = 0;
	for (let i = 0; i < combinedBytes.length; i++) {
		const byte = combinedBytes[i];
		lrc = (lrc + byte) & 0xff;
	}

	return lrc;
};
